// ABA "ORIGEM" da Gestao de Vendas — de onde vieram os compradores do mes (pedido dele, 16/10/2026:
// *"Acrescente uma aba aqui 'Origem'. Nela coloque um grafico de pizza com os criterios de 'Como
// conheceu a loja'"*). A resposta e' marcada no Enviar ao Caixa (migrations 0322/0323).
//
// BI-PARTIDA (pedido dele, com print): a pizza a ESQUERDA, a relacao dos contatos a DIREITA, com
// Data, Contato e Origem e um campo de pesquisa acima. ⚠️ A relacao traz TODOS os compradores do
// mes (a mesma lista da rota que monta a pizza): os respondidos primeiro e, embaixo, os "sem
// resposta" — ate a v2026.10.16.27 eram so' os respondidos, e a lista abriu vazia quando ele
// procurou um cliente que tinha acabado de comprar.
//
// ⚠️ PIZZA porque ele pediu pizza — a forma e' especificacao dele, nao escolha minha.
// ⚠️ A cor segue a ORIGEM, nunca a posicao no ranking: o indice e' o da lista completa (ordem do
// cadastro), entao trocar de mes nao repinta ninguem. Ele e' daltonico (verde/vermelho), e por
// isso a cor nunca carrega o dado sozinha: a legenda traz nome, quantidade e percentual, e a
// relacao escreve a origem por extenso.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { formatDataSP } from '@fiodecobre/shared';
import { api } from '../../lib/api';

interface Fatia { id: number; nome: string; qtd: number }
interface Linha {
  chave: string; nome: string; origem_id: number | null; origem: string; em: string | null; comprou_em: string | null;
  /** Onde a celula grava: o CONTATO quando ha um (fonte unica, migration 0346), senao o pedido da venda. */
  contato_id: number | null; orcamento_id: number;
}
interface Resposta {
  mes: number; ano: number;
  fatias: Fatia[];
  com_resposta: number; sem_resposta: number; compradores: number;
  linhas: Linha[];
}

// Paleta categorica validada (slots 1..8, em ordem fixa — nunca ciclada). A 9a origem em diante cai
// num cinza, e a legenda continua dizendo qual e' qual.
const CORES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
const corDa = (idx: number) => CORES[idx] ?? '#8a8a8a';

const R = 110;
// Folga em volta do disco pro percentual das fatias estreitas, que vai do lado de FORA (pedido dele,
// 17/10/2026: "no grafico, coloque para exibir o percentual").
const CX = 160;
const CY = 135;
// Abaixo desta fracao a fatia nao comporta o texto dentro (7% em R=110 da ~30px de arco no raio do rotulo).
const FRACAO_ROTULO_DENTRO = 0.1;

// Texto dentro da fatia: preto ou branco, o que contrastar mais com a cor dela (WCAG).
function corTextoSobre(hex: string): string {
  const canal = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * canal(1) + 0.7152 * canal(3) + 0.0722 * canal(5);
  return (L + 0.05) / 0.05 >= 1.05 / (L + 0.05) ? '#000' : '#fff';
}

function arco(inicio: number, fim: number): string {
  // Fatia unica (100%) nao se desenha como arco: o ponto inicial e o final coincidem.
  if (fim - inicio >= Math.PI * 2 - 1e-6) {
    return `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R} Z`;
  }
  const x1 = CX + R * Math.sin(inicio);
  const y1 = CY - R * Math.cos(inicio);
  const x2 = CX + R * Math.sin(fim);
  const y2 = CY - R * Math.cos(fim);
  const grande = fim - inicio > Math.PI ? 1 : 0;
  return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${grande} 1 ${x2} ${y2} Z`;
}

const pct = (n: number, total: number) =>
  total > 0 ? `${((n / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—';

export function OrigemClientes({ mes, ano, sinalRecarga }: { mes: number; ano: number; sinalRecarga: number }) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [foco, setFoco] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  // ── A celula Origem e' a PORTA de corrigir (pedido dele, 16/10/2026, com print no "sem resposta"
  // do WILSON: "tornar clicavel para mudar"). A aba ja e' so' de admin, e o admin marca livremente.
  // ⛔ O menu e' `fixed` com `getBoundingClientRect`, nunca `absolute`: a lista rola por dentro e
  // cortaria o menu (regra da casa). Abre pra cima quando nao cabe embaixo.
  const [menu, setMenu] = useState<{ linha: Linha; left: number; top: number } | null>(null);
  const [gravando, setGravando] = useState<string | null>(null);
  const [erroCelula, setErroCelula] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setDados(await api.get<Resposta>('/api/origens-cliente/estatistica', { query: { mes, ano } }));
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar');
    }
  }, [mes, ano]);

  useEffect(() => { void carregar(); }, [carregar, sinalRecarga]);

  // A relacao ocupa ate o fim da janela (ele desenhou o retangulo ate o rodape) e rola por dentro.
  // ⚠️ Altura MEDIDA em runtime, nao constante de CSS: o topo da lista depende dos avisos do topo
  // da pagina, que abrem e fecham.
  // ⛔ O fundo e' o da AREA QUE ROLA (o `main` da pagina), nao o da janela: medido em producao, o
  // `main` termina 21px acima da janela (o rodape da versao), e com a janela como regua a lista
  // passava do fundo. Com os avisos do topo abertos nao ha espaco — o piso de 240 faz a pagina
  // rolar, em vez de uma lista de 3 linhas.
  const listaRef = useRef<HTMLDivElement | null>(null);
  const [alturaLista, setAlturaLista] = useState(400);
  // ⛔ E a medida tem que se REFAZER quando a area muda de tamanho: a barra de avisos do topo chega
  // DEPOIS (busca propria), empurra a pagina e encolhe o `main` — medido em producao, a lista ficou
  // com a altura de antes dos avisos e passou 39px do fundo. O `ResizeObserver` no `main` pega isso
  // e tambem o abrir/recolher dos avisos.
  useLayoutEffect(() => {
    const el = listaRef.current;
    if (!el) return;
    let rolavel: HTMLElement | null = el.parentElement;
    while (rolavel && !/(auto|scroll)/.test(getComputedStyle(rolavel).overflowY)) rolavel = rolavel.parentElement;
    const medir = () => {
      const fundo = rolavel ? rolavel.getBoundingClientRect().bottom : window.innerHeight;
      // `+ scrollTop`: se a area ja estiver rolada, o topo medido subiu junto — volta pra posicao real.
      const topo = el.getBoundingClientRect().top + (rolavel?.scrollTop ?? 0);
      // 36 = o que vem DEPOIS da lista ate o fim da area: o respiro do painel (12), a borda e o
      // `p-4` da pagina (16), com folga. ⚠️ Mais o padding de baixo da PROPRIA area que rola: ele
      // entra no `scrollHeight` sem aparecer em `getBoundingClientRect` de nenhum filho — medido,
      // sem ele a pagina rolava 9px com tudo dentro do fundo.
      const padFundo = rolavel ? parseFloat(getComputedStyle(rolavel).paddingBottom) || 0 : 0;
      setAlturaLista(Math.max(240, Math.floor(fundo - topo - 36 - padFundo)));
    };
    medir();
    const obs = new ResizeObserver(medir);
    if (rolavel) obs.observe(rolavel);
    window.addEventListener('resize', medir);
    return () => { obs.disconnect(); window.removeEventListener('resize', medir); };
  }, [dados]);

  // Fecha ao rolar/redimensionar/Esc: a posicao foi medida uma vez e ficaria pendurada no lugar errado.
  useEffect(() => {
    if (!menu) return;
    const fechar = () => setMenu(null);
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('resize', fechar);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('resize', fechar);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('keydown', tecla);
    };
  }, [menu]);

  function abrirMenu(l: Linha, el: HTMLElement) {
    const r = el.getBoundingClientRect();
    const alturaMenu = 32 * ((dados?.fatias.length ?? 5) + 1) + 12;
    const cabeAbaixo = window.innerHeight - r.bottom > alturaMenu + 8;
    setErroCelula(null);
    setMenu({ linha: l, left: r.left, top: cabeAbaixo ? r.bottom + 2 : Math.max(8, r.top - alturaMenu - 2) });
  }

  async function escolher(l: Linha, origemId: number | null) {
    setMenu(null);
    if (origemId === l.origem_id) return;
    setGravando(l.chave);
    setErroCelula(null);
    try {
      if (l.contato_id) {
        await api.put(`/api/origens-cliente/contato/${l.contato_id}/corrigir`, { origem_cliente_id: origemId });
      } else {
        // Venda sem contato (sem telefone, ou ficha sem WhatsApp): a origem mora no pedido daquela venda.
        await api.put(`/api/origens-cliente/venda/${l.orcamento_id}`, { origem_cliente_id: origemId, cliente_id: 0 });
      }
      await carregar(); // a pizza e o contador mudam junto
    } catch (e: any) {
      // Falha fica NA TELA ate a proxima tentativa (convencao da casa).
      setErroCelula(`${l.nome}: ${e?.corpo?.erro ?? e?.message ?? 'nao deu pra gravar'}`);
    } finally {
      setGravando(null);
    }
  }

  if (erro) return <div className="text-sm text-red-700">{erro}</div>;
  if (!dados) return <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Carregando...</div>;

  const total = dados.com_resposta;
  // A cor vem do indice na lista COMPLETA (ordem do cadastro), calculado antes de qualquer filtro.
  const comCor = dados.fatias.map((f, i) => ({ ...f, cor: corDa(i) }));
  const corPorOrigem = new Map(comCor.map((f) => [f.id, f.cor]));
  // Legenda e pizza: so' origens com comprador, da maior pra menor (pedido dele, 16/10/2026).
  // Empate fica na ordem do cadastro (`sort` estavel). O menu da celula segue com a lista inteira.
  const desenhaveis = comCor.filter((f) => f.qtd > 0).sort((a, b) => b.qtd - a.qtd);
  let angulo = 0;
  const arcos = desenhaveis.map((f) => {
    const ini = angulo;
    angulo += (f.qtd / total) * Math.PI * 2;
    const meio = (ini + angulo) / 2;
    const dentro = f.qtd / total >= FRACAO_ROTULO_DENTRO;
    const raio = desenhaveis.length === 1 ? 0 : dentro ? R * 0.62 : R + 8;
    const rx = CX + raio * Math.sin(meio);
    return {
      ...f,
      d: arco(ini, angulo),
      rotulo: {
        x: rx,
        y: CY - raio * Math.cos(meio),
        dentro,
        // Fora do disco o texto cresce PRA LONGE dele: a esquerda ancora no fim, a direita no comeco.
        ancora: (dentro || Math.abs(rx - CX) < 4 ? 'middle' : rx < CX ? 'end' : 'start') as 'middle' | 'end' | 'start',
      },
    };
  });

  const termo = busca.trim().toUpperCase();
  const visiveis = termo
    // "sem resposta" tambem se pesquisa: e' o texto que a linha mostra na coluna Origem.
    ? dados.linhas.filter((l) => l.nome.toUpperCase().includes(termo) || (l.origem || 'SEM RESPOSTA').toUpperCase().includes(termo))
    : dados.linhas;

  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3" style={{ backgroundColor: 'var(--panel-bg)', color: 'var(--panel-fg)' }}>
      <div className="text-[13px] mb-3">
        <b className="tabular-nums" style={{ color: 'var(--num-destaque)' }}>{dados.compradores}</b> compradores no mes
        {' · '}<b className="tabular-nums">{dados.com_resposta}</b> com a origem respondida
        {' · '}<b className="tabular-nums">{dados.sem_resposta}</b> sem resposta
        <span style={{ color: 'var(--text-muted)' }}> (a pergunta existe desde 16/10/2026)</span>
      </div>

      {/* ⚠️ `minmax(0,1fr)` nas duas: `1fr` puro tem piso `auto` e a coluna com o item mais largo
          come a outra ([DA-602]). Abaixo de `lg` empilha. */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-start">
        {/* ── ESQUERDA: a pizza e a legenda ─────────────────────────────────────────────────── */}
        <div className="min-w-0">
          {total === 0 ? (
            <div className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>
              Nenhum comprador deste mes tem a origem respondida ainda. A pergunta "Como conheceu a
              loja?" fica no Enviar ao Caixa.
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-6">
              <svg width={CX * 2} height={CY * 2} role="img" aria-label="Origem dos compradores do mes" style={{ display: 'block', flexShrink: 0 }}>
                {arcos.map((a) => (
                  <path
                    key={a.id}
                    d={a.d}
                    fill={a.cor}
                    // 2px da cor do painel entre as fatias: separa vizinhas sem depender so' do matiz.
                    stroke="var(--panel-bg)"
                    strokeWidth={2}
                    opacity={foco == null || foco === a.id ? 1 : 0.35}
                    onMouseEnter={() => setFoco(a.id)}
                    onMouseLeave={() => setFoco(null)}
                    style={{ cursor: 'default', transition: 'opacity 120ms' }}
                  >
                    <title>{`${a.nome}: ${a.qtd} (${pct(a.qtd, total)})`}</title>
                  </path>
                ))}
                {arcos.map((a) => (
                  <text
                    key={`r${a.id}`}
                    x={a.rotulo.x}
                    y={a.rotulo.y}
                    textAnchor={a.rotulo.ancora}
                    dominantBaseline="central"
                    fontSize={13}
                    fontWeight={700}
                    fill={a.rotulo.dentro ? corTextoSobre(a.cor) : 'var(--panel-fg)'}
                    opacity={foco == null || foco === a.id ? 1 : 0.35}
                    style={{ pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {pct(a.qtd, total)}
                  </text>
                ))}
              </svg>

              {/* A legenda E' a tabela: nome, quantidade e percentual por origem — a cor so' identifica. */}
              <table className="text-[13px]" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)' }}>
                    <th className="text-left font-semibold pb-1 pr-6">Origem</th>
                    <th className="text-right font-semibold pb-1 pr-4">Compradores</th>
                    <th className="text-right font-semibold pb-1">%</th>
                  </tr>
                </thead>
                <tbody>
                  {desenhaveis.map((f) => (
                    <tr
                      key={f.id}
                      onMouseEnter={() => setFoco(f.id)}
                      onMouseLeave={() => setFoco(null)}
                      style={{ fontWeight: foco === f.id ? 700 : 400 }}
                    >
                      <td className="py-1 pr-6">
                        <span className="inline-flex items-center gap-2">
                          <span style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: f.cor, display: 'inline-block', flexShrink: 0 }} />
                          {f.nome}
                        </span>
                      </td>
                      <td className="py-1 pr-4 text-right tabular-nums">{f.qtd}</td>
                      <td className="py-1 text-right tabular-nums">{pct(f.qtd, total)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid rgba(128,128,128,0.35)' }}>
                    <td className="pt-1.5 pr-6 font-semibold">Total respondido</td>
                    <td className="pt-1.5 pr-4 text-right tabular-nums font-semibold">{total}</td>
                    <td className="pt-1.5 text-right tabular-nums">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── DIREITA: a relacao dos contatos ───────────────────────────────────────────────── */}
        <div className="min-w-0">
          {/* Filtro + contador + lista, o padrao da casa. */}
          <div className="flex items-center gap-3 mb-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Pesquisar contato ou origem..."
                className="w-full pl-8 pr-8 py-1.5 text-sm"
              />
              {busca && (
                <button
                  type="button"
                  onClick={() => setBusca('')}
                  aria-label="Limpar pesquisa"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <span className="text-[12px] whitespace-nowrap tabular-nums" style={{ color: 'var(--text-muted)' }}>
              {termo ? `${visiveis.length} de ${dados.linhas.length}` : `${dados.linhas.length}`} {dados.linhas.length === 1 ? 'contato' : 'contatos'}
            </span>
          </div>

          <div ref={listaRef} className="overflow-y-auto rounded-lg border border-gray-200" style={{ maxHeight: alturaLista }}>
            <table className="tabela w-full">
              <thead>
                <tr>
                  <th style={{ position: 'sticky', top: 0, zIndex: 1, width: 110 }}>Data</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 1 }}>Contato</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 1, width: 190 }}>Origem</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-6" style={{ color: 'var(--text-muted)' }}>
                      {termo ? 'Nenhum contato com esse nome ou origem.' : 'Nenhum comprador neste mes.'}
                    </td>
                  </tr>
                ) : visiveis.map((l) => (
                  <tr key={l.chave}>
                    <td className="tabular-nums">{l.em ? formatDataSP(l.em) : '—'}</td>
                    <td className="truncate" title={l.nome}>{l.nome}</td>
                    <td>
                      {/* A celula inteira e' o botao — com a seta, pra se ler como "escolher". */}
                      <button
                        type="button"
                        onClick={(e) => abrirMenu(l, e.currentTarget)}
                        disabled={gravando === l.chave}
                        title="Clique para mudar a origem"
                        className="w-full flex items-center justify-between gap-2 rounded px-1.5 py-0.5 -mx-1.5 text-left hover:bg-black/5"
                      >
                        {l.origem_id == null ? (
                          <span style={{ color: 'var(--text-muted)' }}>sem resposta</span>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <span style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: corPorOrigem.get(l.origem_id) ?? '#8a8a8a', display: 'inline-block', flexShrink: 0 }} />
                            {l.origem}
                          </span>
                        )}
                        {gravando === l.chave
                          ? <Loader2 size={13} className="animate-spin flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
                          : <ChevronDown size={13} className="flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {erroCelula && (
            <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-[12px] text-red-700">{erroCelula}</div>
          )}
        </div>
      </div>

      {/* O menu de origens da celula clicada. Fecha no clique fora (`onMouseDown` no fundo). */}
      {menu && (
        <>
          <div className="fixed inset-0 z-[1190]" onMouseDown={() => setMenu(null)} />
          <div
            className="fixed z-[1200] rounded-lg border border-gray-300 bg-white shadow-xl py-1 text-[13px]"
            style={{ left: menu.left, top: menu.top, minWidth: 220 }}
          >
            {dados.fatias.map((f) => {
              const atual = menu.linha.origem_id === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => escolher(menu.linha, f.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-800 hover:bg-blue-50 ${atual ? 'font-bold' : ''}`}
                >
                  <span style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: corPorOrigem.get(f.id) ?? '#8a8a8a', display: 'inline-block', flexShrink: 0 }} />
                  <span className="flex-1">{f.nome}</span>
                  {atual && <Check size={13} />}
                </button>
              );
            })}
            {/* Desmarcar: volta a "sem resposta" e apaga a data. */}
            <div className="border-t border-gray-200 my-1" />
            <button
              type="button"
              onClick={() => escolher(menu.linha, null)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-500 hover:bg-gray-50 ${menu.linha.origem_id == null ? 'font-bold' : ''}`}
            >
              <span style={{ width: 10, display: 'inline-block' }} />
              <span className="flex-1">sem resposta</span>
              {menu.linha.origem_id == null && <Check size={13} />}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
